// Global
import {
  ImageField,
  Text,
  useSitecoreContext,
  Field,
  TextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { Options, Splide } from '@splidejs/react-splide';
import React, { useEffect, useRef, useState } from 'react';

// Lib
import { HeroComponents, ProductAndArticleComponent } from 'lib/templates/Feature.Dart.model';
import { ComponentProps } from 'lib/component-props';
import { DartPages } from 'lib/templates/Project.Dart.model';
import { ItemEx } from 'lib/templates/_.Sitecore.Override';

// Local
import ProductCarousel, {
  ProductInformationProps,
} from 'components/authorable/Carousel/ProductCarousel';
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import Button from 'helpers/Button/Button';
import ImageWrapper, { SizedImageField } from 'helpers/ImageWrapper/ImageWrapper';
import Label, { ColorTypes } from 'helpers/Label/Label';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import ShareButton from 'helpers/ShareButton/ShareButton';
import { useRealPathName } from 'lib/utils/use-real-pathname';
import { tailwindVariants } from './HeroArticleTailwind';
import { Common } from 'lib/templates/Foundation.Dart.model';
import { useTheme } from 'lib/context/ThemeContext';
import { ComponentVariants } from 'lib/context/ComponentVariants';

export type Hero = HeroComponents.RenderingParameters.HeroArticle;
export type HeroArticleProps = ComponentProps &
  ProductAndArticleComponent.Articles.ArticleInformation;
export type CategoryInfo = ItemEx & DartPages.CategoryListPage;
type Label = Common.Label.Label;
export type LabelInfo = ItemEx & Common.Label.Label;

type Alignment = 'Left' | undefined;

const HeroArticle = (props: HeroArticleProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const enableRTL = sitecoreContext?.enableRTL as boolean | undefined;
  const direction = enableRTL ? 'rtl' : 'ltr';

  const mainSlider: Options = {
    type: 'fade',
    rewind: false,
    pagination: false,
    arrows: false,
    autoplay: false,
    resetProgress: false,
    direction: direction,
  };

  const thumbSlider: Options = {
    arrows: false,
    fixedWidth: 120,
    fixedHeight: 120,
    gap: 16,
    rewind: false,
    isNavigation: true,
    perPage: 3,
    pagination: false,

    breakpoints: {
      600: {
        fixedWidth: 80,
        fixedHeight: 80,
      },
      768: {
        gap: 12,
      },
    },
  };

  // for storybook
  // const articleDetailPage = sitecoreContext?.route as DartPages.ArticleDetailPage;
  // const articleInfo = articleDetailPage?.fields?.productInformation;
  // const context = articleInfo?.fields;

  const articleDetailPage = sitecoreContext?.route as DartPages.ArticleDetailPage;
  const articleInfo = articleDetailPage?.fields?.articleInformation;
  const context = articleInfo?.fields;
  const categoryInfo = articleDetailPage?.fields?.category as CategoryInfo[];
  const categories = Array.isArray(categoryInfo)
    ? categoryInfo?.map((c: CategoryInfo) => c.fields?.categoryNameForSearchIndex?.value)?.join('|')
    : '';
  const subCategoryInfo = articleDetailPage?.fields?.subCategory as CategoryInfo;
  const filterLabels = articleDetailPage?.fields?.filterLabels as Label[];
  const filterLabelPipeString = filterLabels?.map((c) => c.fields?.title?.value)?.join('|');

  // Theme name
  const { themeName } = useTheme();
  const componentVariants = ComponentVariants();

  const {
    description,
    ctaLink,
    headline,
    stat,
    authorImage,
    authorName,
    publishDate,
    isFeatured,
    primaryImage,
  } = context || {};
  const { alignContent } = props?.params || {};
  const alignment = alignContent !== 'Left';

  // State
  const [initialLoad, setInitialLoad] = useState(0);

  // Path
  const path = useRealPathName();

  useEffect(() => {
    const randomValue = Math.floor(Math.random() * 100) + 1;
    setInitialLoad(randomValue);
  }, [path]);

  const formatDate = (date: Field<string> | undefined) => {
    const dateString = date?.value || '';
    const [year, month, day] = dateString.split('T')[0].split('-');

    // Format the date as MM/DD/YYYY
    const formattedDate = `${month}/${day}/${year}`;
    return { value: formattedDate };
  };

  const publishedTimeStamp = (date: Field<string> | undefined) => {
    const pubDate = new Date(date?.value || '');
    return { value: pubDate.getTime() };
  };
  const {
    base,
    totalWrapper,
    heroArticleWrapper,
    heroArticleTitle,
    heroArticleDescription,
    authorWrapper,
    authorNameDateWrapper,
    heroAuthorName,
    heroAuthorDate,
    heroAuthorImage,
    stateWrapper,
    stateItem,
    stateInnerClass,
    stateCallOut,
    stateLabel,
    statDivider,
    productInformationLeftWrapper,
    heroArticleDescriptionWrapper,
    heroArticleCTAWrapper,
    labelWrapper,
    shareWrapper,
  } = tailwindVariants({
    alignment: alignment as unknown as Alignment,
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
  });

  const mainSliderRef = useRef<Splide>(null);
  const thumbnailSliderRef = useRef<Splide>(null);

  const prodInfo: HeroArticleProps = {
    rendering: { componentName: 'HeroArticle' },
    params: {},
    fields: {
      primaryImage: context?.primaryImage,
      secondaryImages: context?.secondaryImages,
      ctaLink: context?.ctaLink,
      description: context?.description,
      headline: context?.headline,
      label: context?.label,
    },
  };

  // To accomodate the styling of the Duck and Baygon
  const labelVariant = {
    Baygon: {
      color: 'white',
      bgColor: 'bg-components-label-color-on-white-background-bg-default',
    },
    Duck: {
      color: 'color',
      bgColor: 'bg-components-article-hero-color-bg',
    },
  };

  // To accomodate the styling of the Duck
  const labelColor =
    themeName == 'Baygon' || themeName == 'Duck' ? labelVariant[themeName]?.color : 'white';
  const bgColor =
    themeName == 'Baygon' || themeName == 'Duck'
      ? labelVariant[themeName]?.bgColor
      : 'bg-components-article-hero-color-bg';

  if (!context) return <>Article Hero</>;
  const pipeSeparatedLabelTitle = Array.isArray(context.label)
    ? context.label?.map((item: LabelInfo) => item?.fields?.link?.value?.text).join('|')
    : '';

  const pipeSeparatedLabelhref = Array.isArray(context.label)
    ? context.label?.map((item: LabelInfo) => item?.fields?.link?.value?.href).join('|')
    : '';
  return (
    <div className={base()} data-component="authorable/heroes/articlehero">
      <Container>
        <div className={totalWrapper()}>
          <div className={heroArticleWrapper()}>
            <div className={labelWrapper()}>
              <>
                {Array.isArray(context.label) &&
                  context.label?.map((item: LabelInfo, index: number) => {
                    return (
                      <>
                        <Label
                          className="article-label"
                          color={labelColor as ColorTypes}
                          backgroundColor={bgColor}
                          link={item?.fields?.link}
                          key={index}
                        />
                      </>
                    );
                  })}
                <span className="product-label hidden">
                  <span data-href={pipeSeparatedLabelhref}>{pipeSeparatedLabelTitle}</span>
                </span>
              </>
              {context?.socialShare?.value && (
                <>
                  <div className={shareWrapper()}>
                    <ShareButton />
                  </div>
                </>
              )}
            </div>
            {headline?.value && (
              <div>
                <Text className={heroArticleTitle()} encode={false} field={headline} tag="h1" />
              </div>
            )}
            {authorImage?.value?.src || authorName?.value ? (
              <>
                <div className={authorWrapper()}>
                  {authorImage?.value?.src && (
                    <ImageWrapper
                      className={heroAuthorImage()}
                      field={authorImage as ImageField | SizedImageField}
                    />
                  )}
                  {authorName?.value && (
                    <div className={authorNameDateWrapper()}>
                      <span className={heroAuthorName()}>
                        <Text encode={false} field={authorName} />
                      </span>
                      {context?.displayPublishDate && context?.displayPublishDate?.value && (
                        <span className={heroAuthorDate()}>
                          <Text encode={false} field={formatDate(publishDate) as TextField} />
                          <input
                            type="hidden"
                            name="hdnPublishedTimeStamp"
                            id="hdnPublishedTimeStamp"
                            value={publishedTimeStamp(publishDate)?.value}
                          />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {context?.displayPublishDate && context?.displayPublishDate?.value && (
                  <div className={authorWrapper()}>
                    <div className={authorNameDateWrapper()}>
                      <span className={heroAuthorDate()}>
                        <Text encode={false} field={formatDate(publishDate) as TextField} />
                        <input
                          type="hidden"
                          name="hdnPublishedTimeStamp"
                          id="hdnPublishedTimeStamp"
                          value={publishedTimeStamp(publishDate)?.value}
                        />
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
            {context?.description?.value && (
              <div className={heroArticleDescriptionWrapper()}>
                <RichTextA11yWrapper className={heroArticleDescription()} field={description} />
              </div>
            )}
            {!!stat?.length && (
              <div className={stateWrapper()}>
                {context?.stat?.map((stat, index: number) => {
                  const isLast = (context?.stat?.length ?? 0) - 1 === index;

                  return (
                    <div key={index} className={stateItem()}>
                      <div className={stateInnerClass()}>
                        <div className={stateCallOut()}>
                          <Text encode={false} field={stat?.fields?.statLabel} tag="p" />
                        </div>
                        <div className={stateLabel()}>
                          <Text encode={false} field={stat?.fields?.statData} tag="span" />
                        </div>
                      </div>
                      {!isLast && <div className={statDivider()}></div>}
                    </div>
                  );
                })}
              </div>
            )}

            {ctaLink?.value?.href && (
              <div className={heroArticleCTAWrapper()}>
                <Button
                  href={ctaLink?.value?.href}
                  linktype={ctaLink?.value?.linktype}
                  label={ctaLink?.value?.text}
                  tag="a"
                  target={ctaLink?.value?.target}
                  type="filled"
                >
                  <LinkWrapper field={ctaLink} />
                </Button>
              </div>
            )}
          </div>
          {primaryImage?.value?.src && (
            <div className={productInformationLeftWrapper()}>
              <ProductCarousel
                mainSliderRef={mainSliderRef}
                thumbnailSliderRef={thumbnailSliderRef}
                props={prodInfo as ProductInformationProps}
                mainSlider={mainSlider}
                thumbSlider={thumbSlider}
                key={path + initialLoad}
                carouselNavigation={componentVariants?.heroArticle?.carousel?.navigation}
                carouselPagination={componentVariants?.heroArticle?.carousel?.pagination}
              />
            </div>
          )}
        </div>
        <input type="hidden" name="hdnArticleCategory" id="hdnArticleCategory" value={categories} />
        <input
          type="hidden"
          name="hdnArticleSubCategory"
          id="hdnArticleSubCategory"
          value={subCategoryInfo?.fields?.categoryNameForSearchIndex?.value}
        />
        <input
          type="hidden"
          name="hdnIsFeatured"
          id="hdnIsFeatured"
          value={isFeatured?.value?.toString()}
        />
        <input
          type="hidden"
          name="hdnListingPage"
          id="hdnListingPage"
          value={articleDetailPage?.fields?.mainListingPage?.name}
        />
        <input
          type="hidden"
          name="hdnDisplayPublishDate"
          id="hdnDisplayPublishDate"
          value={props?.fields?.displayPublishDate?.value?.toString()}
        />
        <input
          type="hidden"
          name="hdnfilterLabel"
          id="hdnfilterLabel"
          value={filterLabelPipeString}
        />
      </Container>
    </div>
  );
};
export default HeroArticle;
